import React, { ReactNode, useEffect, useState } from 'react';
import './DownloadReport.scss';
import classNames from 'classnames';
import {useTenantApi} from '@/hooks/useTenantApi';
import { create as openModal } from 'react-modal-promise';
import { ByzzerModalMask } from '@/components/form/ByzzerModal';
import { LegacyByzzerButton, LegacyByzzerCheckbox, LegacyByzzerSelect } from '@/components/form';
import {
    downloadReportHeading,
    downloadReportDescription,
    downloadOptions,
    disabledDownloadOptions,
    disabledOptionMessage,
    downloadReportFooterNote,
    mandatoryTiles,
} from '@/config/reportViewer.config';
import { showErrorModal } from '@/utils';

type DownloadReportProps = {
    actionId: number;
    onResolve: (result: boolean) => void;
}

export type TileList = {
    tileName: string;
    tileOption: typeof downloadOptions[number];
};

export function DownloadReport({ 
    actionId, 
    onResolve 
}: DownloadReportProps) {
    const { fetchReportTileInfo } = useTenantApi();
    const [loading, setLoading] = useState<boolean>(false);
    const [busy, setBusy] = useState<boolean>(false);
    const [tileList, setTileList] = useState<Record<string, typeof downloadOptions[number]>[]>([]);
    const [selectedTileList, setSelectedTileList] = useState<TileList[]>([]);
    const [heading, setHeading] = useState<ReactNode>(<>&nbsp;</>);
    const baseClassName = 'download-report';
    const classes = classNames('byzzer-modal-wrapper', `${baseClassName}__wrapper`);

    useEffect(() => {
        loadTileInfo();
    }, []);

    function onCloseClick() {
        onResolve(false);
    }

    async function loadTileInfo() {
        setHeading(downloadReportHeading);
        try {
            setLoading(true);
            const tileData = await fetchReportTileInfo(actionId);
            if (tileData) {
                setTileList(tileData);
                setSelectedTileList(addAllTiles(tileData));
            }
        } catch (err) {
            setLoading(false);
            onResolve(false);
            showErrorModal(err);
        } finally {
            setLoading(false);
        }
    }

    function addAllTiles(tileData) {
        let currentSelections = [...selectedTileList];
        Object.keys(tileData).forEach((tileName) => {
            if (!mandatoryTiles?.includes(tileName)) {
                currentSelections.push({
                    tileName: tileName,
                    tileOption: tileData[tileName][0] ?? '',
                });
            }
        });
        return currentSelections;
    }

    function changeTile(e, tile, allFlag = false) {
        let currentSelections = [...selectedTileList];
        if (e?.target?.checked) {
            if (allFlag) {
                if (Object.keys(tileList)?.length > 0) {
                    currentSelections = addAllTiles(tileList);
                }
            } else {
                currentSelections.push({
                    tileName: tile,
                    tileOption: tileList[tile][0] ?? '',
                });
            }
        } else {
            if (allFlag) {
                currentSelections = [];
            } else {
                const tileIndex = currentSelections.findIndex((val) => val.tileName === tile);
                currentSelections.splice(tileIndex, 1);
            }
        }
        setSelectedTileList(currentSelections);
    }

    function changeTileOption(e, tile) {
        let currentSelections = [...selectedTileList];
        if (e) {
            const tileIndex = currentSelections.findIndex((val) => val.tileName === tile);
            currentSelections[tileIndex].tileOption = e;
        }
        setSelectedTileList(currentSelections);
    }

    function isTileChecked(tile) {
        return mandatoryTiles?.includes(tile) || selectedTileList.map((item) => item.tileName)?.includes(tile);
    }

    function allChecked() {
        return Object.keys(tileList)
            .filter((tl) => !mandatoryTiles?.includes(tl))
            ?.every((v) => selectedTileList.map((item) => item.tileName)?.includes(v));
    }

    function renderTileList() {
        return Object.keys(tileList).map((tile, key) => (
            <div key={key + tile} className={`${baseClassName}__tiles`}>
                <div className={`${baseClassName}__tiles-names`}>
                    {/* @ts-expect-error: Old component isnt type so it thinks everything is required */}
                    <LegacyByzzerCheckbox
                        disabled={mandatoryTiles?.includes(tile)}
                        checked={isTileChecked(tile)}
                        onChange={(e) => changeTile(e, tile)}
                        label={tile}
                    />
                </div>
                {!mandatoryTiles?.includes(tile) && (
                    <div className={`${baseClassName}__tiles-options`}>
                        {/* @ts-expect-error: Old component isnt type so it thinks everything is required */}
                        <LegacyByzzerSelect
                            value={selectedTileList.find((item) => item.tileName === tile)?.tileOption}
                            placeholder={'Select from the list'}
                            options={downloadOptions as any} // todo: replace this with the ui-components version of ByzzerSelect
                            disabledOptions={downloadOptions.filter(x => !tileList[tile].includes(x))}
                            disabledOptionMessage={disabledOptionMessage}
                            onChange={(e) => changeTileOption(e, tile)}
                            disabled={!selectedTileList.find((item) => item.tileName === tile)?.tileName}
                        />
                    </div>
                )}
            </div>
        ));
    }

    function disableDownload() {
        return (
            selectedTileList?.length === 0 ||
            selectedTileList.filter((item) => item.tileName !== '')?.length >
                selectedTileList.filter((item) => item.tileOption as string !== '')?.length // todo: i dont think tileOption should ever be an empty string? but im not sure...
        );
    }

    function onDownload() {
        setBusy(true);
        sessionStorage.setItem('reportActionId', JSON.stringify(actionId));
        sessionStorage.setItem('reportSelectedTileList', JSON.stringify(selectedTileList));
        window.open('/#/download-report', '_blank');
        setBusy(false);
    }

    return (
        <div className={classes}>
            <div className={`${baseClassName}`}>
                <header className={`${baseClassName}__header`}>
                    <h1 className={`${baseClassName}__header-title`}>{heading}</h1>
                    <i className={`${baseClassName}__close`} onClick={onCloseClick} />
                </header>
                <main className={`${baseClassName}__content`}>
                    {/* @ts-expect-error: Old component isnt type so it thinks everything is required */}
                    <ByzzerModalMask show={loading} />
                    <div className={`${baseClassName}__section`}>
                        <div className={`${baseClassName}__description`}>{downloadReportDescription}</div>
                        <div className={`${baseClassName}__selectall`}>
                            {/* @ts-expect-error: Old component isnt type so it thinks everything is required */}
                            <LegacyByzzerCheckbox
                                checked={allChecked()}
                                onChange={(e) => changeTile(e, '', true)}
                                label={'Select All'}
                            />
                        </div>
                        <div className={`${baseClassName}__tiles-outer`}>{renderTileList()}</div>
                        <div className={`${baseClassName}__actions`}>
                            <LegacyByzzerButton
                                label={'Download'}
                                disabled={disableDownload()}
                                onClick={onDownload}
                                busy={busy}
                            />
                        </div>
                        <div className={`${baseClassName}__footer-note`}>{downloadReportFooterNote}</div>
                    </div>
                </main>
            </div>
        </div>
    );
}

export const openDownloadReport = openModal(DownloadReport as any);
